import { CommonModule } from '@angular/common';
import { AfterViewChecked, AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, map, take } from 'rxjs';
import { CONFIG, DataService, DEFAULT_CONFIG, ExplorerComponent, ExplorerService, INode, NgeExplorerConfig, View, VIEWS } from 'ngx-explorer';
import { ConflictResolution, DocumentService } from 'src/app/services/document.service';
import { IconModule } from 'src/app/icon/icon.module';
import { toDocumentHref } from '../eula/eula-content';
import { DocumentConflictDialogComponent } from './document-conflict-dialog/document-conflict-dialog.component';
import { DocumentFolderPickerDialogComponent } from './document-folder-picker-dialog/document-folder-picker-dialog.component';
import { DocumentNameDialogComponent } from './document-name-dialog/document-name-dialog.component';
import { DocumentDataService } from './document-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { MaterialModule } from 'src/app/material.module';
import { DIALOG_WIDTH } from 'src/app/shared/constants';
import { DocumentPickerType, IDocumentConflictDialog, IDocEmptyContextMenu, IDocumentFolderPickerDialog, IDocumentItem, IDocItemContextMenu, IDocumentNameDialog, IDocumentPickerDialogData, IDocumentPickerResult } from 'src/app/shared/interfaces/IDocument';

@Component({
  selector: 'app-documents',
  imports: [CommonModule, TranslateModule, ExplorerComponent, IconModule, MaterialModule],
  providers: [
    ExplorerService,
    { provide: DataService, useClass: DocumentDataService },
    {
      provide: CONFIG,
      useFactory: (translate: TranslateService): NgeExplorerConfig => ({
        ...DEFAULT_CONFIG,
        homeNodeName: translate.instant('root'),
        defaultView: 'List'
      }),
      deps: [TranslateService]
    }
  ],
  templateUrl: './documents.component.html',
  styleUrl: './documents.component.scss'
})
export class DocumentsComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  isPickerMode = false;
  filterType: DocumentPickerType = 'all';
  private static readonly MAX_DISPLAY_NAME_LENGTH = 17;

  @ViewChild('contextMenuUploader') uploaderRef: ElementRef<HTMLInputElement>;
  @ViewChild('explorerHost') explorerHost: ElementRef<HTMLElement>;

  itemContextMenu: IDocItemContextMenu | null = null;
  emptyContextMenu: IDocEmptyContextMenu | null = null;
  selectedFile: IDocumentItem | null = null;

  canDownload$ = this.explorerService.selection$.pipe(map((n) => n.length > 0 && n.every((node) => node.isLeaf)));
  canDelete$ = this.explorerService.selection$.pipe(map((n) => n.length > 0));
  canRename$ = this.explorerService.selection$.pipe(map((n) => n.length === 1));

  private currentChildren: INode[] = [];
  private currentSelection: INode[] = [];

  constructor(
    private explorerService: ExplorerService,
    private dataService: DataService,
    private documentService: DocumentService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private dialog: MatDialog,
    @Inject(CONFIG) protected config: NgeExplorerConfig,
    @Inject(VIEWS) protected views: View[],
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData?: IDocumentPickerDialogData,
    @Optional() public dialogRef?: MatDialogRef<DocumentsComponent>) {}

  ngOnInit() {
    if (this.dialogData) {
      this.isPickerMode = true;
      this.filterType = this.dialogData.filterType || 'all';
    }

    if (this.dataService instanceof DocumentDataService) {
      this.dataService.filterType = this.filterType;
    }

    this.explorerService.openNode();
    this.explorerService.openedDir$.subscribe((node) => {
      this.currentChildren = node?.children ?? [];
      if (node) {
        this.expandTreeAncestors(node);
      }
    });
    this.explorerService.selection$.subscribe((selection) => {
      this.currentSelection = selection;
      const leafSelection = selection.filter((n) => n.isLeaf);
      this.selectedFile = leafSelection.length === 1 ? (leafSelection[0].data as IDocumentItem) : null;
    });
  }

  ngAfterViewInit() {
    // ngx-explorer's own DragDropDirective (applied to the icons/list view root) calls
    // explorerService.upload() directly from its native 'drop' handler - hardcoded, not
    // something a template binding can override - so a drag-and-dropped file bypasses the
    // conflict-resolution dialog entirely. Intercepting here, on an ancestor, in the capture
    // phase, means this handler runs and can stop propagation before the library's own
    // bubble-phase listener (registered on the descendant) ever sees the event.
    this.explorerHost.nativeElement.addEventListener('drop', this.onNativeDrop, true);
  }

  ngOnDestroy() {
    this.explorerHost?.nativeElement.removeEventListener('drop', this.onNativeDrop, true);
  }

  private onNativeDrop = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    this.explorerService.openedDir$.pipe(take(1)).subscribe((node) => {
      const parentPath = (node?.data as IDocumentItem | undefined)?.path ?? null;
      this.performUpload(parentPath, fileArray);
    });
  };

  get dialogTitle(): string {
    switch (this.filterType) {
      case 'image':
        return 'select image';
      case 'video':
        return 'select video';
      default:
        return 'select file';
    }
  }

  onConfirmPicker() {
    if (!this.selectedFile || !this.dialogRef) return;
    const url = toDocumentHref(this.selectedFile.path!);
    this.dialogRef.close({ file: this.selectedFile, url } as IDocumentPickerResult);
  }

  onCancelPicker() {
    this.dialogRef?.close();
  }

  onExplorerDblClick(event: MouseEvent) {
    if (!this.isPickerMode) return;
    const target = event.target as HTMLElement;
    const itemEl = target.closest<HTMLElement>('.nxe-icons-wrapper, tbody tr');
    if (!itemEl) return;

    const index = this.getItemIndex(itemEl);
    const node = index !== null ? this.currentChildren[index] : undefined;
    if (node && node.isLeaf && node.data) {
      this.selectedFile = node.data as IDocumentItem;
      this.onConfirmPicker();
    }
  }

  // Navigating to a folder (double-click in the list/grid, breadcrumb, etc.) only opens it in
  // the main pane - it doesn't touch the left tree's own expand state, so the newly opened
  // folder can end up highlighted there but hidden behind a still-collapsed ancestor. Walk up
  // the chain and expand whichever ancestors aren't already, so the current folder is always
  // visible in the tree, not just marked as selected.
  private expandTreeAncestors(node: INode) {
    let parentId = node.parentId;
    while (parentId) {
      const parent = this.explorerService.getNode(parentId);
      if (!parent) break;
      if (!parent.expanded) {
        this.explorerService.expand(parent.id);
      }
      parentId = parent.parentId;
    }
  }

  // ngx-explorer renders plain "{{ item.name }}" text with no slot for a custom display name,
  // and its List view leaves the Type/Size/Last Modified columns empty entirely - so both are
  // patched by rewriting the rendered DOM text directly. This never touches the underlying
  // node/name used by rename, selection, etc.
  ngAfterViewChecked() {
    const host = this.explorerHost?.nativeElement;
    if (!host) return;

    host.querySelectorAll<HTMLElement>('.nxe-icon-text').forEach((el) => {
      // Read the real current name from the sibling [title] binding, not from this element's own
      // textContent: once we overwrite textContent below, we replace Angular's own interpolated
      // text node, so on a later re-render (e.g. navigating back out of a folder, where @for
      // reuses this same DOM position for a different item) Angular can no longer update what's
      // visible here - it keeps updating a text node that's no longer attached to the DOM. The
      // wrapper's [title] is a plain, untouched attribute binding, so it always reflects the
      // real current item name and is safe to use as the source of truth.
      const wrapperInner = el.closest<HTMLElement>('.nxe-icons-wrapper-inner');
      const realName = wrapperInner?.title ?? el.textContent ?? '';
      if (realName === el.dataset['truncatedFor']) {
        return; // unchanged since we last wrote it - nothing new to truncate
      }

      const truncated = this.middleTruncate(realName);
      el.dataset['truncatedFor'] = realName;
      if (truncated !== el.textContent) {
        el.textContent = truncated;
      }
    });

    this.fillListColumns(host);
    this.renderSelectionCheckboxes(host);
  }

  // Neither the icons nor the list view has a template slot for a selection checkbox, so one is
  // injected per item here, matching the same DOM-patch approach used above for names/columns.
  // Checked state and toggling both go straight through explorerService.selection$ so every
  // existing selection-driven action (toolbar buttons, context menu, move/copy/delete/download)
  // works for a multi-selection built via checkboxes with no further changes.
  private renderSelectionCheckboxes(host: HTMLElement) {
    host.querySelectorAll<HTMLElement>('.nxe-icons-wrapper').forEach((wrapper, index) => {
      this.ensureSelectionCheckbox(wrapper, this.currentChildren[index]);
    });

    host.querySelectorAll<HTMLElement>('.nxe-list-wrapper tbody tr').forEach((row, index) => {
      const firstCell = row.querySelector<HTMLElement>('td');
      if (firstCell) {
        this.ensureSelectionCheckbox(firstCell, this.currentChildren[index]);
      }
    });
  }

  private ensureSelectionCheckbox(container: HTMLElement, node: INode | undefined) {
    if (!node) return;

    let checkbox = container.querySelector<HTMLInputElement>(':scope > input.document-select-checkbox');
    if (!checkbox) {
      checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'document-select-checkbox';
      checkbox.onclick = (event) => event.stopPropagation();
      container.insertBefore(checkbox, container.firstChild);
    }

    // Reassigned every cycle, not just on creation: @for reuses this same DOM position for a
    // different item across renders (e.g. after navigating folders), so the handler must always
    // be bound to whichever node currently occupies this position - same underlying issue as the
    // name-truncation fix above, just for an event handler closure instead of a text node.
    checkbox.onchange = () => this.toggleSelection(node, checkbox!.checked);
    checkbox.checked = this.currentSelection.some((n) => n.id === node.id);
  }

  private toggleSelection(node: INode, checked: boolean) {
    const next = checked ? [...this.currentSelection, node] : this.currentSelection.filter((n) => n.id !== node.id);
    this.explorerService.select(next);
  }

  private fillListColumns(host: HTMLElement) {
    const rows = host.querySelectorAll<HTMLElement>('.nxe-list-wrapper tbody tr');
    if (rows.length === 0 || this.currentChildren.length === 0) return;

    rows.forEach((row) => {
      const cells = row.querySelectorAll<HTMLElement>('td');
      if (cells.length < 4) return;

      const name = cells[0].textContent?.trim() ?? '';
      const node = this.currentChildren.find((c) => c.name === name);
      const data = node?.data as IDocumentItem | undefined;
      if (!data) return;

      this.setCellText(cells[1], data.isDirectory ? this.translateService.instant('folder') : this.translateService.instant('file'));
      this.setCellText(cells[2], !data.isDirectory && data.sizeBytes != null ? this.formatSize(data.sizeBytes) : '');
      this.setCellText(cells[3], data.lastModified ? new Date(data.lastModified).toLocaleString() : '');
    });
  }

  private setCellText(cell: HTMLElement, text: string) {
    if (cell.textContent !== text) {
      cell.textContent = text;
    }
  }

  private formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;

    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = -1;
    do {
      value /= 1024;
      unitIndex++;
    } while (value >= 1024 && unitIndex < units.length - 1);

    return `${value.toFixed(1)} ${units[unitIndex]}`;
  }

  private middleTruncate(name: string, maxLength = DocumentsComponent.MAX_DISPLAY_NAME_LENGTH): string {
    if (name.length <= maxLength) return name;

    const dotIndex = name.lastIndexOf('.');
    const hasExtension = dotIndex > 0 && dotIndex < name.length - 1;
    const extension = hasExtension ? name.slice(dotIndex) : '';
    const base = hasExtension ? name.slice(0, dotIndex) : name;

    const keepLength = Math.max(4, maxLength - extension.length - 1);
    const headLength = Math.ceil(keepLength * 0.6);
    const tailLength = keepLength - headLength;

    if (base.length <= keepLength) return name;

    const head = base.slice(0, headLength);
    const tail = tailLength > 0 ? base.slice(base.length - tailLength) : '';
    return `${head}…${tail}${extension}`;
  }

  setView(view: string) {
    this.explorerService.currentView$.next(view);
  }

  onContextMenu(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.nxe-content-middle')) {
      // outside the file/folder pane (tree, toolbar, breadcrumbs) - keep the browser's default menu
      return;
    }

    event.preventDefault();
    this.closeContextMenus();

    const itemEl = target.closest<HTMLElement>('.nxe-icons-wrapper, tbody tr');
    if (!itemEl) {
      this.emptyContextMenu = { x: event.clientX, y: event.clientY };
      return;
    }

    const index = this.getItemIndex(itemEl);
    const node = index !== null ? this.currentChildren[index] : undefined;
    const alreadySelected = !!node && this.currentSelection.some((n) => n.id === node.id);

    if (!alreadySelected) {
      // right-clicking an item outside the current (possibly multi-item) selection replaces it,
      // matching normal file-explorer behavior; right-clicking within the existing selection
      // keeps it as-is so a checkbox-built multi-selection survives opening the context menu
      itemEl.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    }

    this.explorerService.selection$.pipe(take(1)).subscribe((selection) => {
      if (selection.length > 0) {
        this.itemContextMenu = {
          x: event.clientX,
          y: event.clientY,
          count: selection.length,
          canDownload: selection.every((n) => n.isLeaf)
        };
      }
    });
  }

  private getItemIndex(itemEl: HTMLElement): number | null {
    const parent = itemEl.parentElement;
    if (!parent) return null;
    const index = Array.from(parent.children).indexOf(itemEl);
    return index === -1 ? null : index;
  }

  onBackdropContextMenu(event: MouseEvent) {
    event.preventDefault();
    this.closeContextMenus();
  }

  @HostListener('document:keydown.escape')
  closeContextMenus() {
    this.itemContextMenu = null;
    this.emptyContextMenu = null;
  }

  async createDir() {
    this.closeContextMenus();
    const name = await this.promptDocumentName({ title: 'new folder', label: 'folder name' });
    if (name) {
      this.explorerService.createDir(name);
    }
  }

  refresh() {
    this.explorerService.refresh();
    this.refreshExpandedTreeNodes();
    this.closeContextMenus();
  }

  // explorerService.refresh() only reloads the currently open directory's listing - it doesn't
  // touch other folders already expanded in the left tree, which then keep showing stale
  // content. Walk the tree ourselves and re-expand every node that's currently expanded so its
  // children get refetched too.
  private refreshExpandedTreeNodes() {
    this.explorerService.root$.pipe(take(1)).subscribe((root) => {
      root.children?.forEach((child) => this.refreshExpandedTreeNode(child));
    });
  }

  private refreshExpandedTreeNode(node: INode) {
    if (node.isLeaf) return;
    if (node.expanded) {
      this.explorerService.expand(node.id);
    }
    node.children?.forEach((child) => this.refreshExpandedTreeNode(child));
  }

  rename() {
    this.closeContextMenus();
    this.explorerService.selection$.pipe(take(1)).subscribe(async (selection) => {
      const node = selection[0];
      if (!node) return;
      const newName = await this.promptDocumentName({ title: 'rename', label: 'new name', value: node.name });
      if (newName) {
        this.explorerService.rename(newName);
      }
    });
  }

  private promptDocumentName(config: IDocumentNameDialog): Promise<string | undefined> {
    const dialogRef = this.dialog.open(DocumentNameDialogComponent, { width: config.width ?? DIALOG_WIDTH.SMALLEST, data: config });
    return firstValueFrom(dialogRef.afterClosed());
  }

  moveTo() {
    this.closeContextMenus();
    this.moveOrCopy(false);
  }

  copyTo() {
    this.closeContextMenus();
    this.moveOrCopy(true);
  }

  private moveOrCopy(isCopy: boolean) {
    this.explorerService.selection$.pipe(take(1)).subscribe(async (selection) => {
      if (selection.length === 0) return;

      const destination = await this.promptDestinationFolder(isCopy ? 'copy to' : 'move to');
      if (destination === undefined) return;

      const targets = selection.map((node) => {
        const data = node.data as IDocumentItem;
        return { path: data.path!, isDirectory: !!data.isDirectory };
      });

      this.performMoveOrCopy(isCopy, targets, destination || null);
    });
  }

  private performMoveOrCopy(isCopy: boolean, targets: { path: string; isDirectory: boolean }[], destination: string | null, resolution?: ConflictResolution) {
    const request$ = isCopy
      ? this.documentService.copyDocuments(targets, destination, resolution)
      : this.documentService.moveDocuments(targets, destination, resolution);

    request$.subscribe({
      next: async (res) => {
        if (res.conflicts?.length) {
          const chosen = await this.promptConflictResolution(res.conflicts);
          if (chosen) {
            this.performMoveOrCopy(isCopy, targets, destination, chosen);
          }
          return;
        }

        this.toastService.success(this.translateService.instant(isCopy ? 'copy successfully' : 'move successfully'));
        this.refresh();
      },
      error: ({ error }) => {
        this.toastService.error(this.translateService.instant(error.Message));
      }
    });
  }

  private async promptDestinationFolder(title: string): Promise<string | undefined> {
    const dialogRef = this.dialog.open(DocumentFolderPickerDialogComponent, {
      width: DIALOG_WIDTH.SMALLEST,
      data: { title } as IDocumentFolderPickerDialog
    });
    const result = await firstValueFrom(dialogRef.afterClosed());
    return result?.path !== undefined ? (result.path as string) : undefined;
  }

  private async promptConflictResolution(conflicts: string[]): Promise<ConflictResolution | undefined> {
    const dialogRef = this.dialog.open(DocumentConflictDialogComponent, {
      width: DIALOG_WIDTH.SMALLEST,
      data: { conflicts } as IDocumentConflictDialog
    });
    return firstValueFrom(dialogRef.afterClosed());
  }

  remove() {
    this.closeContextMenus();
    this.toastService.confirmDelete({
      title: 'delete',
      content: 'do you want to delete this',
      width: DIALOG_WIDTH.SMALLEST,
      onConfirm: () => this.explorerService.remove()
    });
  }

  download() {
    this.closeContextMenus();
    this.explorerService.selection$.pipe(take(1)).subscribe((selection) => {
      const files = selection.filter((n) => n.isLeaf);
      if (files.length === 0) return;

      if (files.length === 1) {
        this.explorerService.download();
        return;
      }

      files.forEach((node) => this.dataService.downloadFile(node.data).subscribe());
    });
  }

  openUploader() {
    this.uploaderRef.nativeElement.click();
    this.closeContextMenus();
  }

  handleFiles(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);
    this.explorerService.openedDir$.pipe(take(1)).subscribe((node) => {
      const parentPath = (node?.data as IDocumentItem | undefined)?.path ?? null;
      this.performUpload(parentPath, files);
    });
    input.value = '';
  }

  private performUpload(parentPath: string | null, files: File[], resolution?: ConflictResolution) {
    this.documentService.uploadDocuments(parentPath, files, resolution).subscribe({
      next: async (res) => {
        if (res.conflicts?.length) {
          const chosen = await this.promptConflictResolution(res.conflicts);
          if (chosen) {
            this.performUpload(parentPath, files, chosen);
          }
          return;
        }

        this.refresh();
      },
      error: ({ error }) => {
        this.toastService.error(this.translateService.instant(error.Message));
      }
    });
  }
}
